import type { CustomExperimentSpec, RenderHandle } from '@learnforge/demo-render/harness';
import { defineExperiment } from '@learnforge/demo-render/harness';
import {
  createFormulaLegend,
  createSvgEl,
  createZoomViewport,
  type FormulaLegendEntry,
  type ViewportView,
} from '@learnforge/demo-render';
import { createAxes } from '../render/axes';
import { createCurve } from '../render/curve';
import { samplePolar, type PolarFigureSpec } from '../trig/polar';
import { createActionBar } from '../action-bar';
import { createSliderRow, createToggleRow, createDropdownRow } from '@learnforge/demo-kit';
import type { TrigContext } from '../types';
import type { TrigKey } from '../i18n';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type PolarId = 'circle' | 'rose-3' | 'cardioid' | 'limacon';

const BASE_VIEW = { x: -6.6, y: -3, width: 13.2, height: 6 };
const ORBIT_X = -3.3;
const TWO_PI = 2 * Math.PI;
const POLAR_FORMULA: Record<PolarId, string> = {
  circle: 'r = 1',
  'rose-3': 'r = sin(3θ)',
  cardioid: 'r = 1 + cos(θ)',
  limacon: 'r = 1 − 2cos(θ)',
};

// ---------------------------------------------------------------------------
// State — model only (no DOM refs)
// ---------------------------------------------------------------------------

interface State {
  omega: number;
  plotSin: boolean;
  plotCos: boolean;
  smallAngle: boolean;
  polarId: PolarId;
  theta: number;
  playing: boolean;
}

// ---------------------------------------------------------------------------
// Spec factory
// ---------------------------------------------------------------------------

function buildSpec(): CustomExperimentSpec<State, TrigKey, TrigContext> {
  return {
    labelKey: 'experiment.circular-polar.label',
    demoId: 'trigonometry',
    advancedKey: 'circular-polar',

    initialState(): State {
      return {
        omega: 1.0,
        plotSin: true,
        plotCos: false,
        smallAngle: false,
        polarId: 'circle',
        theta: 0,
        playing: false,
      };
    },

    createRender(ctx, host, controls): RenderHandle<State> {
      // ---- Paused mode (VR freeze: ?paused=1) ----
      const paused = new URLSearchParams(window.location.search).get('paused') === '1';

      // ---- Transient animation state (NOT in State) ----
      let lastTs = 0;
      let rafId = 0;

      // ---- Transient mirror of polarId used by animation callbacks ----
      // update() keeps this in sync; tick/stepBy/reset use it inside setState
      // callbacks where they don't receive the polar dropdown value directly.
      let polarIdLive: PolarId = 'circle';

      // ---- SVG stage ----
      const svg = createSvgEl('svg', {
        viewBox: `${BASE_VIEW.x} ${BASE_VIEW.y} ${BASE_VIEW.width} ${BASE_VIEW.height}`,
        preserveAspectRatio: 'xMidYMid meet',
        class: 'tg-svg',
        'font-size': '0.15',
      });
      host.appendChild(svg);

      // Y-flip so math coords point up
      const world = createSvgEl('g', { transform: 'scale(1 -1)' });
      svg.appendChild(world);

      // ---- Left axes (orbit half) ----
      const leftAxes = createAxes({
        xDomain: [BASE_VIEW.x, 0],
        yDomain: [-3, 3],
        showGrid: false,
        targetTicks: { x: 5, y: 6 },
        i18n: ctx.i18n,
      });
      world.appendChild(leftAxes.group);

      // ---- Right axes (plot half) ----
      const rightAxes = createAxes({
        xDomain: [0, BASE_VIEW.x + BASE_VIEW.width],
        yDomain: [-3, 3],
        xTickMode: 'pi',
        targetTicks: { x: 5, y: 6 },
        i18n: ctx.i18n,
      });
      world.appendChild(rightAxes.group);

      // ---- Left half — orbit group, centred at (-3.3, 0) ----
      const orbit = createSvgEl('g', { transform: `translate(${ORBIT_X} 0)` });
      world.appendChild(orbit);

      // Polar figure path
      const figurePath = createSvgEl('path', {
        class: 'tg-polar',
        'vector-effect': 'non-scaling-stroke',
      });
      orbit.appendChild(figurePath);

      // Animated particle
      const particle = createSvgEl('circle', {
        class: 'tg-particle',
        cx: 1,
        cy: 0,
        r: 0.15,
      });
      orbit.appendChild(particle);

      // ---- Right half — live curves ----
      const curveGroup = createSvgEl('g', { transform: 'translate(0 0)' });
      world.appendChild(curveGroup);

      // Sin/cos curves: θ ∈ [0, 2π] plotted unscaled (x = θ in world units)
      const CURVE_X_DOMAIN: [number, number] = [0, TWO_PI];

      const sinCurve = createCurve(curveGroup, {
        fn: (x: number) => Math.sin(x),
        xDomain: CURVE_X_DOMAIN,
        samples: 240,
        className: 'tg-curve-live-sin',
      });

      const cosCurve = createCurve(curveGroup, {
        fn: (x: number) => Math.cos(x),
        xDomain: CURVE_X_DOMAIN,
        samples: 240,
        className: 'tg-curve-live-cos',
      });
      cosCurve.element.style.display = 'none'; // initial: plotCos = false

      // Playhead line — vertical line at current θ position in the right half
      const playhead = createSvgEl('line', {
        class: 'tg-playhead',
        x1: 0,
        x2: 0,
        y1: -3,
        y2: 3,
        'vector-effect': 'non-scaling-stroke',
      });
      curveGroup.appendChild(playhead);

      // ---- Small-angle inset ----
      const smallAngleGroup = createSvgEl('g', { class: 'tg-smallangle' });
      world.appendChild(smallAngleGroup);
      smallAngleGroup.style.display = 'none';

      const saGroup = createSvgEl('g', { transform: `translate(${ORBIT_X} 0)` });
      smallAngleGroup.appendChild(saGroup);

      const SA_DOMAIN: [number, number] = [-Math.PI / 4, Math.PI / 4];
      const saScale = 2.5 / (Math.PI / 4);

      const saSinCurve = createCurve(saGroup, {
        fn: (x: number) => Math.sin(x),
        xDomain: SA_DOMAIN,
        samples: 120,
        className: 'tg-curve-live-sin',
      });
      saSinCurve.element.setAttribute('transform', `scale(${saScale} ${saScale})`);

      const saLinCurve = createCurve(saGroup, {
        fn: (x: number) => x,
        xDomain: SA_DOMAIN,
        samples: 2,
        className: 'tg-curve-live-cos',
      });
      saLinCurve.element.setAttribute('transform', `scale(${saScale} ${saScale})`);

      // Tick marks at ±π/12 in the small-angle inset
      for (const tx of [-Math.PI / 12, Math.PI / 12]) {
        const tick = createSvgEl('line', {
          x1: tx * saScale,
          x2: tx * saScale,
          y1: -0.08,
          y2: 0.08,
          class: 'tg-tick-x',
          'vector-effect': 'non-scaling-stroke',
        });
        saGroup.appendChild(tick);
      }

      // ---- Legend ----
      const legend = createFormulaLegend(host);

      // ---- Zoom / pan: each half keeps its own axes on its side of x = 0 ----
      const viewport = createZoomViewport(svg, {
        base: BASE_VIEW,
        yUp: true,
        fontSize: 0.15,
        onChange: (v: ViewportView) => {
          const chromeScale = 1 / v.zoom;
          const [x0, x1] = v.xDomain;
          leftAxes.group.style.display = x0 < 0 ? '' : 'none';
          if (x0 < 0) leftAxes.update({ xDomain: [x0, 0], yDomain: v.yDomain, chromeScale });
          rightAxes.group.style.display = x1 > 0 ? '' : 'none';
          if (x1 > 0) rightAxes.update({ xDomain: [0, x1], yDomain: v.yDomain, chromeScale });
          const plotDomain: [number, number] = [0, Math.max(TWO_PI, x1)];
          const samples = Math.min(2400, Math.max(240, Math.round((240 * plotDomain[1]) / TWO_PI)));
          sinCurve.setView({ xDomain: plotDomain, samples });
          cosCurve.setView({ xDomain: plotDomain, samples });
          playhead.setAttribute('y1', String(v.yDomain[0]));
          playhead.setAttribute('y2', String(v.yDomain[1]));
        },
      });

      // ---- Action bar ----
      const actionBarHandle = createActionBar(ctx.actionBar, {
        buttons: [
          { id: 'play', labelKey: 'cp.play', onClick: () => togglePlay() },
          { id: 'step', labelKey: 'cp.step', onClick: () => stepBy(Math.PI / 12) },
          { id: 'reset', labelKey: 'cp.reset', onClick: () => reset() },
          { id: 'view', labelKey: 'view.reset', onClick: () => viewport.reset() },
        ],
        i18n: ctx.i18n,
      });

      // ---- Panel controls (built once) ----
      const omegaRow = createSliderRow({
        id: 'cp-omega',
        labelKey: 'cp.omega',
        i18n: ctx.i18n,
        min: 0,
        max: 3,
        step: 0.05,
        value: 1.0,
        format: (v) => v.toFixed(2),
        onChange: (v) => {
          controls.setState((s) => ({ ...s, omega: v }));
        },
      });
      ctx.panel.appendChild(omegaRow.element);

      const sinToggle = createToggleRow({
        labelKey: 'cp.plot.y',
        i18n: ctx.i18n,
        checked: true,
        onChange: (v) => {
          controls.setState((s) => ({ ...s, plotSin: v }));
        },
      });
      ctx.panel.appendChild(sinToggle.element);

      const cosToggle = createToggleRow({
        labelKey: 'cp.plot.x',
        i18n: ctx.i18n,
        checked: false,
        onChange: (v) => {
          controls.setState((s) => ({ ...s, plotCos: v }));
        },
      });
      ctx.panel.appendChild(cosToggle.element);

      const polarDropdown = createDropdownRow<PolarId>({
        id: 'cp-polar',
        labelKey: 'cp.polar.label',
        i18n: ctx.i18n,
        options: [
          { value: 'circle', labelKey: 'cp.polar.circle' },
          { value: 'rose-3', labelKey: 'cp.polar.rose' },
          { value: 'cardioid', labelKey: 'cp.polar.cardioid' },
          { value: 'limacon', labelKey: 'cp.polar.limacon' },
        ],
        value: 'circle',
        onChange: (v) => {
          controls.setState((s) => ({ ...s, polarId: v }));
        },
      });
      ctx.panel.appendChild(polarDropdown.element);

      const saToggle = createToggleRow({
        labelKey: 'cp.smallAngle',
        i18n: ctx.i18n,
        checked: false,
        onChange: (v) => {
          controls.setState((s) => ({ ...s, smallAngle: v }));
        },
      });
      ctx.panel.appendChild(saToggle.element);

      // ---------------------------------------------------------------------------
      // Helpers
      // ---------------------------------------------------------------------------

      function sampleAt(thetaVal: number, pid: PolarId): number {
        switch (pid) {
          case 'circle':
            return 1;
          case 'rose-3':
            return Math.sin(3 * thetaVal);
          case 'cardioid':
            return 1 + Math.cos(thetaVal);
          case 'limacon':
            return 1 - 2 * Math.cos(thetaVal);
        }
      }

      function redrawFigure(smallAngleOn: boolean, pid: PolarId): void {
        if (smallAngleOn) {
          orbit.style.display = 'none';
          smallAngleGroup.style.display = '';
          return;
        }
        orbit.style.display = '';
        smallAngleGroup.style.display = 'none';

        const figureSpec: PolarFigureSpec = { id: pid };
        const pts = samplePolar(figureSpec, 360);
        let d = '';
        for (let i = 0; i < pts.length; i++) {
          d += `${i === 0 ? 'M' : 'L'} ${pts[i]!.x.toFixed(4)} ${pts[i]!.y.toFixed(4)} `;
        }
        if (pts.length > 0) d += 'Z';
        figurePath.setAttribute('d', d.trim());
      }

      function positionParticle(thetaVal: number, pid: PolarId): void {
        const r = sampleAt(thetaVal, pid);
        particle.setAttribute('cx', String((r * Math.cos(thetaVal)).toFixed(5)));
        particle.setAttribute('cy', String((r * Math.sin(thetaVal)).toFixed(5)));
      }

      function updatePlayhead(thetaVal: number): void {
        const x = thetaVal % TWO_PI;
        playhead.setAttribute('x1', String(x.toFixed(4)));
        playhead.setAttribute('x2', String(x.toFixed(4)));
      }

      // ---------------------------------------------------------------------------
      // Playback controls
      // ---------------------------------------------------------------------------

      function togglePlay(): void {
        if (paused) return;
        controls.setState((s) => {
          const nextPlaying = !s.playing;
          if (nextPlaying) {
            lastTs = performance.now();
            rafId = controls.raf(tick);
          } else {
            controls.cancelRaf(rafId);
            rafId = 0;
          }
          actionBarHandle.setLabel('play', nextPlaying ? 'cp.pause' : 'cp.play');
          return { ...s, playing: nextPlaying };
        });
      }

      function stepBy(delta: number): void {
        if (paused) return;
        controls.setState((s) => {
          const nextTheta = (s.theta + delta) % (2 * Math.PI);
          positionParticle(nextTheta, polarIdLive);
          updatePlayhead(nextTheta);
          return { ...s, theta: nextTheta };
        });
      }

      function reset(): void {
        controls.cancelRaf(rafId);
        rafId = 0;
        actionBarHandle.setLabel('play', 'cp.play');
        controls.setState((s) => {
          positionParticle(0, polarIdLive);
          updatePlayhead(0);
          return { ...s, theta: 0, playing: false };
        });
      }

      function tick(now: number): void {
        if (paused) return;
        controls.setState((s) => {
          if (!s.playing) return s;
          const dt = Math.min((now - lastTs) / 1000, 0.1);
          lastTs = now;
          const nextTheta = (s.theta + s.omega * dt) % (2 * Math.PI);
          positionParticle(nextTheta, polarIdLive);
          updatePlayhead(nextTheta);
          rafId = controls.raf(tick);
          return { ...s, theta: nextTheta };
        });
      }

      // ---- Initial render ----
      redrawFigure(false, 'circle');
      positionParticle(0, 'circle');
      updatePlayhead(0);

      // ---------------------------------------------------------------------------
      // update — reconcile view from state (called by harness after every setState)
      // ---------------------------------------------------------------------------

      function update(state: State): void {
        // Keep polarIdLive in sync so animation tick callbacks have access to it
        polarIdLive = state.polarId;

        sinCurve.element.style.display = state.plotSin ? '' : 'none';
        cosCurve.element.style.display = state.plotCos ? '' : 'none';

        redrawFigure(state.smallAngle, state.polarId);
        positionParticle(state.theta, state.polarId);
        updatePlayhead(state.theta);

        const entries: FormulaLegendEntry[] = state.smallAngle
          ? [
              { id: 'small-angle-sin', formula: 'y = sin(x)', color: 'var(--accent)' },
              {
                id: 'small-angle-linear',
                formula: 'y = x',
                color: 'var(--red)',
                lineStyle: 'dashed',
              },
            ]
          : [{ id: 'polar', formula: POLAR_FORMULA[state.polarId], color: 'var(--ink)' }];
        if (!state.smallAngle && state.plotSin)
          entries.push({ id: 'plot-sin', formula: 'y = sin(θ)', color: 'var(--accent)' });
        if (!state.smallAngle && state.plotCos)
          entries.push({
            id: 'plot-cos',
            formula: 'x = cos(θ)',
            color: 'var(--red)',
            lineStyle: 'dashed',
          });
        legend.setEntries(entries);

        // Keep play-button label in sync whenever state is applied
        actionBarHandle.setLabel('play', state.playing ? 'cp.pause' : 'cp.play');
      }

      return {
        update,
        dispose(): void {
          // Stop animation. The harness also auto-cancels all tracked rAFs on
          // dispose, but cancelling explicitly here keeps the playing=false
          // semantics clean so no late tick fires between dispose() and harness
          // cleanup.
          controls.cancelRaf(rafId);
          rafId = 0;

          // Destroy bespoke resources
          sinCurve.destroy();
          cosCurve.destroy();
          saSinCurve.destroy();
          saLinCurve.destroy();
          leftAxes.destroy();
          rightAxes.destroy();
          legend.destroy();
          viewport.dispose();
          actionBarHandle.destroy();
          // Note: do NOT call svg.remove() or replaceChildren() on ctx.root /
          // ctx.panel / ctx.actionBar — the harness owns that teardown.
        },
      };
    },

    applyShotState(state: State): State {
      // ?paused=1 in the URL freezes the animation at the initial frame; no
      // state override needed — the guard inside createRender handles it.
      return state;
    },
  };
}

export const experiment = defineExperiment(buildSpec());

// Alias for the existing index.ts + test imports (behaviorally identical).
export const mountCircularPolar = experiment.mount.bind(experiment);
